import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Avatar,
  Card,
  CardContent,
  Grid,
  Stack,
  Divider,
  Tooltip,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Key as KeyIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  AccountCircle as AccountIcon,
} from "@mui/icons-material";
import ChangePasswordModal from "./ChangePasswordModal";
import { apiCall } from "../../services/ApiCall";
import config from "../../config/config.json";

function Profile() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Original state variables (unchanged)
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [profileData, setProfileData] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState(false);

  const primaryColor = "#0d6efd";

  // Original event handlers (unchanged logic)
  const handleChangePassword = () => {
    setShowPasswordModal(true);
  };

  const handleCloseModal = () => {
    setShowPasswordModal(false);
  };

  const handleEditClick = (field) => {
    setEditingField(field);
  };

  const handleSaveClick = async () => {
    // Enhanced save logic with API call
    setLoading(true);
    try {
      const { isSuccess } = await apiCall(
        `${config.POST_LOGIN_API_URL}updateProfileData`,
        {},
        profileData,
        "POST"
      );
      
      if (isSuccess) {
        setSnackbar({ open: true, message: "Profile updated successfully!", severity: "success" });
        setEditingField(null);
      } else {
        setSnackbar({ open: true, message: "Failed to update profile", severity: "error" });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setSnackbar({ open: true, message: "An error occurred while updating profile", severity: "error" });
    }
    setLoading(false);
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    // Optionally refresh data to revert changes
    getProfileData();
  };

  // Original API function (unchanged logic)
  const getProfileData = async () => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}getProfileData`,
        {},
        {},
        "GET"
      );

      if (isSuccess) {
        setProfileData(data.data);
      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
      setSnackbar({ open: true, message: "Failed to load profile data", severity: "error" });
    }
  };

  // Original useEffect (unchanged logic)
  useEffect(() => {
    getProfileData();
  }, []);

  const ProfileField = ({ label, field, value, icon, type = "text", placeholder = "" }) => (
    <Grid item xs={12} sm={6}>
      <Card 
        elevation={0}
        sx={{
          border: `1px solid ${primaryColor}20`,
          borderRadius: 2,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: theme.shadows[2],
            borderColor: `${primaryColor}40`,
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {icon}
            <Typography variant="subtitle2" sx={{ color: 'black', fontWeight: 400 }}>
              {label}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              type={type}
              value={value || ''}
              placeholder={placeholder}
              variant={editingField === field ? "outlined" : "standard"}
              InputProps={{
                readOnly: editingField !== field,
                disableUnderline: editingField !== field,
              }}
              onChange={(e) => setProfileData({ ...profileData, [field]: e.target.value })}
              size={isSmall ? "small" : "medium"}
              sx={{
                '& .MuiInput-root': {
                  '&:before': {
                    borderBottom: editingField !== field ? 'none' : '1px solid rgba(0, 0, 0, 0.42)',
                  },
                  '&:hover:not(.Mui-disabled):before': {
                    borderBottom: editingField !== field ? 'none' : '2px solid rgba(0, 0, 0, 0.87)',
                  },
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: primaryColor + '40',
                  },
                  '&:hover fieldset': {
                    borderColor: primaryColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: primaryColor,
                  },
                },
                '& .MuiInput-input': {
                  fontWeight: editingField !== field ? 500 : 400,
                  color: editingField !== field ? 'black' : 'inherit',
                },
              }}
            />
            
            {editingField === field ? (
              <Stack direction="row" spacing={1}>
                <Tooltip title="Save">
                  <IconButton 
                    onClick={handleSaveClick}
                    disabled={loading}
                    sx={{ 
                      color: theme.palette.success.main,
                      '&:hover': {
                        backgroundColor: `${theme.palette.success.main}10`,
                      }
                    }}
                    size="small"
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton 
                    onClick={handleCancelEdit}
                    sx={{ 
                      color: theme.palette.error.main,
                      '&:hover': {
                        backgroundColor: `${theme.palette.error.main}10`,
                      }
                    }}
                    size="small"
                  >
                    <EditIcon fontSize="small" sx={{ transform: 'rotate(45deg)' }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : (
              <Tooltip title="Edit">
                <IconButton 
                  onClick={() => handleEditClick(field)}
                  sx={{ 
                    color: 'black',
                    '&:hover': {
                      backgroundColor: `${primaryColor}10`,
                    }
                  }}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%' }}>
      {/* Header Section */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Box sx={{
          color: 'black',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          <Avatar
            sx={{
              width: { xs: 80, sm: 100 },
              height: { xs: 80, sm: 100 },
             backgroundColor: 'rgba(13, 110, 253, 0.05)',
              fontSize: { xs: '2rem', sm: '2.5rem' }
            }}
          >
            <AccountIcon sx={{ fontSize: { xs: '2.5rem', sm: '3rem' ,color:'#0d6efd'} }} />
          </Avatar>
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              {profileData?.name || 'User Profile'}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
              My Site Inspection
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.8 }}>
              {profileData?.email || 'No email provided'}
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<KeyIcon />}
            onClick={handleChangePassword}
            sx={{
              bgcolor: '#0076D3',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              
              minWidth: { xs: '100%', sm: 'auto' }
            }}
          >
            Change Password
          </Button>
        </Box>
      </Paper>

      {/* Profile Form Section */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          bgcolor: `${primaryColor}08`,
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          {/* <PersonIcon sx={{ color: primaryColor, fontSize: 28 }} /> */}
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'black' }}>
            Profile Information
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Grid container spacing={3}>
            <ProfileField
              label="Full Name"
              field="name"
              value={profileData?.name}
              icon={<PersonIcon sx={{ color: 'black', fontSize: 20 }} />}
              type="text"
            />

            <ProfileField
              label="Email Address"
              field="email"
              value={profileData?.email}
              icon={<EmailIcon sx={{ color: 'black', fontSize: 20 }} />}
              type="email"
            />

            <ProfileField
              label="Phone Number"
              field="phone"
              value={profileData?.phone}
              icon={<PhoneIcon sx={{ color: 'black', fontSize: 20 }} />}
              type="tel"
            />

            <ProfileField
              label="Company Name"
              field="company"
              value={profileData?.company}
              icon={<BusinessIcon sx={{ color: 'black', fontSize: 20 }} />}
              type="text"
              placeholder="My Site Inspection"
            />

            <Grid item xs={12}>
              <Card 
                elevation={0}
                sx={{
                  border: `1px solid ${primaryColor}20`,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: theme.shadows[2],
                    borderColor: `${primaryColor}40`,
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationIcon sx={{ color: 'black', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ color: 'black', fontWeight: 600 }}>
                      Company Headquarters Address
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={editingField === 'address' ? 3 : 1}
                      value={profileData?.address || ''}
                      placeholder="Enter Address"
                      variant={editingField === 'address' ? "outlined" : "standard"}
                      InputProps={{
                        readOnly: editingField !== 'address',
                        disableUnderline: editingField !== 'address',
                      }}
                      onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                      size={isSmall ? "small" : "medium"}
                      sx={{
                        '& .MuiInput-root': {
                          '&:before': {
                            borderBottom: editingField !== 'address' ? 'none' : '1px solid rgba(0, 0, 0, 0.42)',
                          },
                          '&:hover:not(.Mui-disabled):before': {
                            borderBottom: editingField !== 'address' ? 'none' : '2px solid rgba(0, 0, 0, 0.87)',
                          },
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: primaryColor + '40',
                          },
                          '&:hover fieldset': {
                            borderColor: primaryColor,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: primaryColor,
                          },
                        },
                        '& .MuiInput-input': {
                          fontWeight: editingField !== 'address' ? 500 : 400,
                          color: editingField !== 'address' ? 'black' : 'inherit',
                        },
                      }}
                    />
                    
                    {editingField === 'address' ? (
                      <Stack direction="row" spacing={1} sx={{ alignSelf: 'flex-start' }}>
                        <Tooltip title="Save">
                          <IconButton 
                            onClick={handleSaveClick}
                            disabled={loading}
                            sx={{ 
                              color: theme.palette.success.main,
                              '&:hover': {
                                backgroundColor: `${theme.palette.success.main}10`,
                              }
                            }}
                            size="small"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton 
                            onClick={handleCancelEdit}
                            sx={{ 
                              color: theme.palette.error.main,
                              '&:hover': {
                                backgroundColor: `${theme.palette.error.main}10`,
                              }
                            }}
                            size="small"
                          >
                            <EditIcon fontSize="small" sx={{ transform: 'rotate(45deg)' }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Tooltip title="Edit">
                        <IconButton 
                          onClick={() => handleEditClick('address')}
                          sx={{ 
                            color: 'black',
                            '&:hover': {
                              backgroundColor: `${primaryColor}10`,
                            },
                            alignSelf: 'flex-start'
                          }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Original Modal (unchanged) */}
      {showPasswordModal && <ChangePasswordModal onClose={handleCloseModal} />}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Profile;

















/*  
import React, { useEffect, useState } from "react";
import "./style.css";
import ChangePasswordModal from "./ChangePasswordModal";
import { apiCall } from "../../services/ApiCall";
import config from "../../config/config.json";
import { MdEdit } from "react-icons/md";
import { FaSave } from "react-icons/fa";

function Profile() {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [profileData, setProfileData] = useState('');
  const [editingField, setEditingField] = useState(null);

  const handleChangePassword = () => {
    setShowPasswordModal(true);
  };

  const handleCloseModal = () => {
    setShowPasswordModal(false);
  };

  const handleEditClick = (field) => {
    setEditingField(field);
  };

  const handleInputChange = (field, value) => {
    setProfileData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleSaveClick = async () => {
    // Save the updated data to the backend
    const { isSuccess } = await apiCall(
      `${config.POST_LOGIN_API_URL}updateProfileData`,
      profileData,
      {},
      "POST"
    );

    if (isSuccess) {
      setEditingField(null);
    } else {
      alert("Failed to save the changes. Please try again.");
    }
  };

  const getProfileData = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getProfileData`,
      {},
      {},
      "GET"
    );

    if (isSuccess) {
      setProfileData(data.data);
    }
  };

  useEffect(() => {
    getProfileData();
  }, []);

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-image"></div>
        <div className="profile-info">
          <h2>{profileData?.name}</h2>
          <div className="namep">My Site Inspection</div>
          <p>{profileData?.email}</p>
        </div>
        <button className="change-password" onClick={handleChangePassword}>
          Change Password
        </button>
      </div>
      <form className="profile-form">
        <div className="form-group">
          <label htmlFor="name">Name</label>
          <div className="input-container">
            <input
              type="text"
              id="name"
              value={profileData?.name}
              readOnly={editingField !== "name"}
              onChange={(e) => handleInputChange("name", e.target.value)}
            />
            {editingField === "name" ? (
              <FaSave className="icon" onClick={handleSaveClick} />
            ) : (
              <MdEdit className="icon" onClick={() => handleEditClick("name")} />
            )}
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <div className="input-container">
            <input
              type="email"
              id="email"
              value={profileData?.email}
              readOnly={editingField !== "email"}
              onChange={(e) => handleInputChange("email", e.target.value)}
            />
            {editingField === "email" ? (
              <FaSave className="icon" onClick={handleSaveClick} />
            ) : (
              <MdEdit className="icon" onClick={() => handleEditClick("email")} />
            )}
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <div className="input-container">
            <input
              type="tel"
              id="phone"
              value={profileData?.phone}
              readOnly={editingField !== "phone"}
              onChange={(e) => handleInputChange("phone", e.target.value)}
            />
            {editingField === "phone" ? (
              <FaSave className="icon" onClick={handleSaveClick} />
            ) : (
              <MdEdit className="icon" onClick={() => handleEditClick("phone")} />
            )}
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="company">Company Name</label>
          <div className="input-container">
            <input
              type="text"
              id="company"
              value={profileData?.company || ''}
              placeholder="My Site Inspection"
              readOnly={editingField !== "company"}
              onChange={(e) => handleInputChange("company", e.target.value)}
            />
            {editingField === "company" ? (
              <FaSave className="icon" onClick={handleSaveClick} />
            ) : (
              <MdEdit className="icon" onClick={() => handleEditClick("company")} />
            )}
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="address">Company Headquarters Address</label>
          <div className="input-container">
            <input
              type="text"
              id="address"
              value={profileData?.address || ''}
              placeholder="Enter Address"
              readOnly={editingField !== "address"}
              onChange={(e) => handleInputChange("address", e.target.value)}
            />
            {editingField === "address" ? (
              <FaSave className="icon" onClick={handleSaveClick} />
            ) : (
              <MdEdit className="icon" onClick={() => handleEditClick("address")} />
            )}
          </div>
        </div>
      </form>
      {showPasswordModal && <ChangePasswordModal onClose={handleCloseModal} />}
    </div>
  );
}

export default Profile;

*/